import { Visibility } from "@mui/icons-material";
import {
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography
} from "@mui/material";
import moment from "moment-timezone";
import React from "react";
import { t } from "ttag";
import Tooltip from "../../components/Tooltip/LightTooltip";
import { IQueueJob, IQueueScheduler, QueueTab } from "../../models/Queues.types";
import QueueSchedulersTable from "./QueueSchedulersTable";
import { describeRetention, hasRetention } from "./queueRetention";
import useStyles from "./QueueJobsTable.style";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const getTabs = (): Array<{ tab: QueueTab; label: string }> => [
  { tab: "latest", label: t`Latest` },
  { tab: "active", label: t`Active` },
  { tab: "waiting", label: t`Waiting` },
  { tab: "waiting-children", label: t`Waiting children` },
  { tab: "prioritized", label: t`Prioritized` },
  { tab: "completed", label: t`Completed` },
  { tab: "failed", label: t`Failed` },
  { tab: "delayed", label: t`Delayed` },
  { tab: "paused", label: t`Paused` },
  { tab: "schedulers", label: t`Schedulers` }
];

interface IQueueJobsTableProps {
  queueName: string;
  queuePrefix?: string;
  status: QueueTab;
  schedulers: IQueueScheduler[];
  schedulersTotal: number;
  /** job counts of the selected queue, for the tab labels */
  counts: { [status: string]: number };
  jobs: IQueueJob[];
  totalCount: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onStatusChange: (status: QueueTab) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onViewJob: (job: IQueueJob) => void;
}

const formatTimestamp = (timestamp?: number) => (timestamp ? moment(timestamp).format("llll") : "-");

// Only finished jobs have anything to expire.
const EXPIRY_TABS: QueueTab[] = ["latest", "completed", "failed"];

const QueueJobsTable: React.FC<IQueueJobsTableProps> = ({
  queueName,
  queuePrefix,
  status,
  schedulers,
  schedulersTotal,
  counts,
  jobs,
  totalCount,
  page,
  pageSize,
  loading,
  onStatusChange,
  onPageChange,
  onPageSizeChange,
  onViewJob
}) => {
  const classes: any = useStyles();

  const handleStatusChange = (_event: unknown, newStatus: QueueTab) => {
    onStatusChange(newStatus);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  const handleViewClick = (job: IQueueJob) => () => {
    onViewJob(job);
  };

  const showExpiry = EXPIRY_TABS.includes(status);

  // The combined view has no count of its own; the schedulers are counted once they are loaded.
  const getTabLabel = (tab: QueueTab, label: string) => {
    if (tab === "schedulers") {
      return schedulersTotal ? `${label} (${schedulersTotal})` : label;
    }

    return tab === "latest" ? label : `${label} (${counts[tab] || 0})`;
  };

  // Completed and failed jobs have different limits, so the note is for a page of one kind only.
  const retentionNote =
    status === "completed" || status === "failed" ? describeRetention(jobs.find((job) => job.retention)?.retention) : "";

  const renderExpiry = (job: IQueueJob) => {
    if (!hasRetention(job.retention)) {
      return "-";
    }

    const description = describeRetention(job.retention);

    return (
      <Tooltip title={description} placement="top">
        <span>{job.expiresAt ? formatTimestamp(job.expiresAt) : t`Newest ${job.retention.count} kept`}</span>
      </Tooltip>
    );
  };

  const renderRows = () => {
    if (!jobs.length) {
      return (
        <TableRow>
          <TableCell colSpan={8 + (status === "latest" ? 1 : 0) + (showExpiry ? 1 : 0)} className={classes.emptyCell}>
            <Typography className={classes.emptyText}>{t`No jobs in this state.`}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return jobs.map((job) => (
      <TableRow hover key={job.id} className={classes.row}>
        <TableCell className={classes.idCell}>{job.id}</TableCell>
        <TableCell className={classes.cell}>{job.name}</TableCell>
        {status === "latest" && <TableCell className={classes.cell}>{job.status}</TableCell>}
        <TableCell className={classes.cell}>{formatTimestamp(job.timestamp)}</TableCell>
        <TableCell className={classes.cell}>{formatTimestamp(job.finishedOn || job.processedOn)}</TableCell>
        {showExpiry && <TableCell className={classes.cell}>{renderExpiry(job)}</TableCell>}
        <TableCell className={classes.cell}>{job.processedBy || ""}</TableCell>
        <TableCell align="right" className={classes.cell}>{job.attemptsMade || 0}</TableCell>
        <TableCell className={classes.failedReason}>{job.failedReason || ""}</TableCell>
        <TableCell align="center" className={classes.cell}>
          <Tooltip title={t`View job details`} placement="top">
            <IconButton size="small" aria-label={t`View job details`} onClick={handleViewClick(job)}>
              <Visibility className={classes.viewIcon} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  const renderPagination = (count: number) => (
    <TablePagination
      component="div"
      className={classes.pagination}
      count={count}
      page={page}
      rowsPerPage={pageSize}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handlePageSizeChange}
    />
  );

  return (
    <Paper elevation={0} className={classes.tablePaper}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {queueName}
          {!!queuePrefix && <span className={classes.prefix}> {queuePrefix}</span>}
        </Typography>
        <Tabs value={status} onChange={handleStatusChange} variant="scrollable" scrollButtons="auto">
          {getTabs().map(({ tab, label }) => (
            <Tab key={tab} value={tab} className={classes.tab} label={getTabLabel(tab, label)} />
          ))}
        </Tabs>
      </div>
      {status === "schedulers" ? (
        <>
          <QueueSchedulersTable schedulers={schedulers} loading={loading} />
          {renderPagination(schedulersTotal)}
        </>
      ) : (
        <>
          {status === "latest" && (
            <Typography className={classes.note}>
              {t`The newest ${pageSize} jobs of each state, out of ${totalCount.toLocaleString()} in all`}
            </Typography>
          )}
          {!!retentionNote && <Typography className={classes.note}>{retentionNote}</Typography>}
          <TableContainer className={classes.tableContainer}>
            {loading && (
              <div className={classes.loadingOverlay}>
                <CircularProgress size={32} />
              </div>
            )}
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell}>{t`JOB ID`}</TableCell>
                  <TableCell className={classes.headCell}>{t`NAME`}</TableCell>
                  {status === "latest" && <TableCell className={classes.headCell}>{t`STATE`}</TableCell>}
                  <TableCell className={classes.headCell}>{t`ADDED`}</TableCell>
                  <TableCell className={classes.headCell}>{t`LAST RUN`}</TableCell>
                  {showExpiry && <TableCell className={classes.headCell}>{t`EXPIRES`}</TableCell>}
                  <TableCell className={classes.headCell}>{t`WORKER`}</TableCell>
                  <TableCell align="right" className={classes.headCell}>{t`ATTEMPTS`}</TableCell>
                  <TableCell className={classes.headCell}>{t`FAILURE REASON`}</TableCell>
                  <TableCell align="center" className={classes.headCell}>{t`VIEW`}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>
          {status !== "latest" && renderPagination(totalCount)}
        </>
      )}
    </Paper>
  );
};

export default QueueJobsTable;
